import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MsalService } from '@azure/msal-angular';
import { NgbModal, NgbModalConfig, NgbModalRef, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { Store } from '@ngrx/store';
import { CommandModel, EditSettingsModel, FilterSettingsModel, GridComponent, IEditCell, IFilter, PageSettingsModel, SearchSettingsModel, ToolbarItems, GridModule } from '@syncfusion/ej2-angular-grids';
import { DataManager, Query } from '@syncfusion/ej2-data';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { AdminService } from 'src/app/services';
import {
  ApplicationState, getApplicationState, GetUsers, UpdateUser, User, SetAdminSection, GetGroups, GetRoles, Group, Role,
  AdminSections, AdminUserView, AdminCreateNewUser, UpdateGroup, UpdateRole, AdminCreateNewGroup, AdminCreateNewRole,
  AdminModalViewMode,
  CustomAdminFilterModalTargets
} from 'src/app/state-management';

declare var $: any;

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
  providers: [NgbModalConfig, NgbModal],
  imports: [GridModule, CommonModule, ReactiveFormsModule, NgbTypeahead, NgSelectModule],
})
export class AdminComponent implements OnInit {

  @ViewChild('userGrid') public userGrid: GridComponent;
  @ViewChild('groupsGrid') public groupGrid: GridComponent;
  @ViewChild('rolesGrid') public roleGrid: GridComponent;

  private ngUnsubscribe: Subject<void> = new Subject();

  public toolbarOptions: ToolbarItems[];
  public editSettings: EditSettingsModel;
  public pageSettings: PageSettingsModel;
  public commands: CommandModel[];

  ddParams: IEditCell;
  public searchOptions: SearchSettingsModel;
  status: object[] = [
    { text: 'Yes', value: true },
    { text: 'No', value: false },
  ];

  userForm = new UntypedFormGroup({
    firstName: new UntypedFormControl('', Validators.required),
    lastName: new UntypedFormControl('', Validators.required),
    email: new UntypedFormControl('', [Validators.required, Validators.email]),
    upn: new UntypedFormControl('', Validators.required),
    groupSelector: new UntypedFormControl([], Validators.required),
    roleSelector: new UntypedFormControl([]),
    active: new UntypedFormControl(true, Validators.required)
  });

  groupForm = new UntypedFormGroup({
    groupName: new UntypedFormControl('', Validators.required),
    groupEmail: new UntypedFormControl('', Validators.email),
    groupActive: new UntypedFormControl(true, Validators.required)
  });

  roleForm = new UntypedFormGroup({
    groupRef: new UntypedFormControl('', Validators.required),
    roleName: new UntypedFormControl('', Validators.required),
    roleEmail: new UntypedFormControl('', Validators.email),
    roleActive: new UntypedFormControl(true, Validators.required)
  });

  application$: Observable<ApplicationState>;
  users: AdminUserView[];
  groups: Group[];
  roles: Role[];
  adminSection: string;
  adminADUserList: AdminUserView[];

  groupAssignmentsMap: Map<string, boolean>;
  roleAssignmentsMap: Map<string, boolean>;
  assignmentMapPreMapFilter: Map<string, boolean>;
  assignmentMapLoaded: Map<string, boolean>;
  filterableUsersList: AdminUserView[];
  selectAllCheckSelected: boolean = true;
  hasOneEntrySelected: boolean = true;
  filterSearchString: string = "";
  noFilterSearchResultsFound = false;
  readonly BLANKS_KEY = "Blanks";

  searchString = '';

  sortOptionsUser: object;
  sortOptionsGroupRole: object;
  shouldShowErrors = false;
  activeFilterSettings: IFilter;
  usersGroupRoleFilterSettings: IFilter;
  filterSettings: FilterSettingsModel;

  filterModalTarget: string;
  isCustomFilterOpen: boolean = false;

  modal: NgbModalRef = null;
  selectedUser: AdminUserView;
  selectedGroup: Group;
  selectedRole: Role;
  modalViewMode: AdminModalViewMode;
  edocsUser = '';
  edocsColor = '';
  edocsAlias = '';

  @ViewChild('contentManagement') contentManagement: TemplateRef<any>;

  public azureUserSearch = (text$: Observable<string>) => new Observable<AdminUserView[]>();
  public edocsAliasValidate = (text$: Observable<string>) => new Observable<any>();

  constructor(
    private store: Store<{}>,
    private modalService: NgbModal,
    config: NgbModalConfig,
    private authService: MsalService,
    private adminService: AdminService) {

    this.application$ = this.store.select(getApplicationState);
    config.backdrop = 'static';
    // this.handleCustomGridFilterClick.bind(this);

    this.groupAssignmentsMap = new Map([[this.BLANKS_KEY, true]]);
    this.roleAssignmentsMap = new Map([[this.BLANKS_KEY, true]]);
    this.assignmentMapLoaded = new Map();
  }


  ngOnInit(): void {
    this.store.dispatch(new GetUsers());
    this.store.dispatch(new GetGroups());
    this.store.dispatch(new GetRoles());

    this.application$
      .pipe(
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(application => {
        this.adminSection = application.admin.section;
        this.groups = application.admin.groups;
        this.roles = application.admin.roles;
        this.users = application.admin.users;

        this.users.forEach(user => {
          const groupNames: string[] = this.getUserAssignedGroupNames(user);
          const roleNames: string[] = this.getUserAssignedRoleNames(user);

          user.groupNamesStr = groupNames.toString().replaceAll(',', ', ');
          user.roleNamesStr = roleNames.toString().replaceAll(',', ', ');

          // if the entries dont exist, then create and set to true, otherwise, set to their current value
          groupNames.forEach(gn => this.groupAssignmentsMap.set(gn, this.groupAssignmentsMap.has(gn) ? this.groupAssignmentsMap.get(gn) : true));
          roleNames.forEach(rn => this.roleAssignmentsMap.set(rn, this.roleAssignmentsMap.has(rn) ? this.roleAssignmentsMap.get(rn) : true));

          if (this.userGrid && this.userGrid.dataSource !== null) {
            this.userGrid.refresh();
          }
        });

        this.filterableUsersList = [...this.users];
      });

    this.editSettings = { allowEditing: true, allowAdding: true, allowDeleting: false, mode: 'Dialog' };
    this.pageSettings = { pageSize: 25 };

    this.commands = [{
      type: 'Edit',
      buttonOption: {
        cssClass: 'e-flat',
        iconCss: 'e-edit e-icons',
        click: this.clickEditIcon.bind(this)
      }
    }];
    this.ddParams = {
      params: {
        dataSource: new DataManager(this.status), fields: { text: 'text', value: 'value' }, query: new Query(),
        actionComplete: () => false
      }
    };

    this.sortOptionsUser = { columns: [{ field: 'firstName', direction: 'Ascending' }] };
    this.sortOptionsGroupRole = { columns: [{ field: 'name', direction: 'Ascending' }] };
    this.searchOptions = {
      fields: ['firstName', 'lastName', 'email', 'edocsAlias', 'upn', 'groupNamesStr', 'roleNamesStr', 'active'],
      operator: 'contains',
      key: this.searchString,
      ignoreCase: true
    };

    this.toolbarOptions = ['Search'];
    this.filterSettings = { type: 'Menu' }
    this.usersGroupRoleFilterSettings = { operator: 'contains' };
    this.activeFilterSettings = { type: 'CheckBox', hideSearchbox: true };

    document.addEventListener('keyup', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.closeModal();
      }
    });

    this.applyCustomGridFilterEventListener();

    this.azureUserSearch = (text$: Observable<string>) => {
      return text$.pipe(
        debounceTime(50),
        distinctUntilChanged(),
        switchMap((name: string) => {
          const request = {
            scopes: ['User.ReadBasic.All'],
            account: this.authService.instance.getActiveAccount()
          };

          if (name.length === 0) {
            return of([]);
          }

          return forkJoin({
            token: from(this.authService.instance.acquireTokenSilent(request)),
            name: of(name)
          });
        }),
        switchMap((response: any) => {
          if (!response.token || !response.name) {
            return of([]);
          }

          return this.adminService.getADFilteredUsers(response.token.accessToken, response.name);
        }),
        map((response: any) => {
          const usersList: AdminUserView[] = [];

          (response.value || []).forEach((userEntry: any) => {
            if (
              (userEntry.givenName && userEntry.surname && userEntry.mail && userEntry.userPrincipalName) &&
              !this.users.some(user => ((user.email || '').toLowerCase() === userEntry.mail.toLowerCase()
                || (user.upn || '').toLowerCase() === userEntry.userPrincipalName.toLowerCase()))
            ) {
              usersList.push({
                firstName: userEntry.givenName.trim(),
                lastName: userEntry.surname.trim(),
                email: userEntry.mail.trim(),
                active: true,
                upn: userEntry.userPrincipalName.trim(),
                groupSelections: [],
                roleSelections: [],
                edocsAlias: ''
              });
            }
          });

          return [...usersList];
        }),
        catchError(() => of([]))
      );
    };

    this.edocsAliasValidate = (text$: Observable<string>) => {
      return text$.pipe(
        debounceTime(50),
        distinctUntilChanged(),
        switchMap((): any => {
          const emailAdd = this.userForm.get('email').value;
          const fullName = this.userForm.get('firstName').value + ' ' + this.userForm.get('lastName').value;

          return this.adminService.validateEdocsAlias(emailAdd, fullName)
            .pipe(
              map((res: any) => {
                console.log(res);
                const usersList: any = [];
                (res).forEach((userEntry: any) => {
                  if (userEntry) {
                    usersList.push({
                      fullName: userEntry.fullName,
                      edocsAlias: userEntry.edocsAlias
                    });
                  }
                });
                return [...usersList];
              }),
              catchError((error: HttpErrorResponse): Observable<AdminUserView[]> => {
                return of([]);
              }),
            );
        })
      );
    };
  }

  getUserAssignedGroupNames(user: AdminUserView) {
    const groupNames: string[] = [];
    user.groupSelections.forEach(groupId =>
      this.groups.filter(group => group.group_Id === groupId).map(group => !groupNames.includes(group.name)
        && groupNames.push(group.name))
    );

    return groupNames;
  }

  getUserAssignedRoleNames(user: AdminUserView) {
    const roleNames: string[] = [];
    user.roleSelections.forEach(roleId =>
      this.roles.filter(role => role.role_Id === roleId).map(role => !roleNames.includes(role.name) && roleNames.push(role.name))
    );

    return roleNames;
  }

  formatter = (x: any) => x.firstName ? x.firstName : x;

  edocsFormatter = (x: any) => x.edocsAlias ? x.edocsAlias : x;

  replaceActiveFilterDisplayValue(args: any) {
    return args["active"] ? "Yes" : "No";
    // return args["active"] ? "Enabled" : "Disabled";
  }

  selectAdminSection(section: string) {
    this.store.dispatch(new SetAdminSection(section));
  }

  onAzureUserSelect(entry: any) {
    this.setUserFormValues(entry.item);
  }

  onEdocsAliasSelect(evt: any) {
    this.edocsAlias = evt.item.edocsAlias;
    this.userForm.get('edocsAlias').setValue(this.edocsAlias);
    this.edocsUser = evt.item.fullName;
    const fName = this.userForm.get('firstName').value.toLowerCase();
    const lName = this.userForm.get('lastName').value.toLowerCase();
    const fullName = fName + ' ' + lName;
    if (evt.item.fullName.toLowerCase() === fullName) {
      this.edocsColor = 'green';
      this.userForm.get('edocsAlias').setErrors(null);
    } else {
      this.edocsColor = 'red';
    }
  }

  validateAlias() {
    const eaValue = this.userForm.get('edocsAlias').value.edocsAlias ? this.userForm.get('edocsAlias').value.edocsAlias : this.userForm.get('edocsAlias').value;
    if (this.edocsAlias.length > 0 && eaValue !== this.edocsAlias) {
      this.edocsUser = null;
      this.userForm.get('edocsAlias').setErrors({ 'incorrect': true });
    } else {
      this.userForm.get('edocsAlias').setErrors(null);
    }
  }

  addUserFormEditControls() {
    this.userForm.addControl('edocsAlias', new UntypedFormControl('', Validators.required));
  }

  removeUserFormEditControls() {
    this.userForm.removeControl('edocsAlias');
  }

  clickEditIcon(event: any) {
    const row = event.currentTarget.closest('.e-row');
    let currentGrid = null;

    if (this.adminSection === AdminSections.Users) {
      currentGrid = this.userGrid;
    } else if (this.adminSection === AdminSections.Groups) {
      currentGrid = this.groupGrid;
    } else {
      currentGrid = this.roleGrid;
    }

    const rowEntry = currentGrid.getRowInfo(row);
    this.editRow(rowEntry);
  }

  editRow(rowEntry: any) {
    const row = rowEntry.rowData;

    this.modalViewMode = AdminModalViewMode.Edit;
    this.addUserFormEditControls();

    this.selectedUser = null;
    this.selectedGroup = null;
    this.selectedRole = null;

    switch (this.adminSection) {
      case AdminSections.Users:
        this.selectedUser = row;
        break;

      case AdminSections.Groups:
        this.selectedGroup = row;
        break;

      case AdminSections.Roles:
        this.selectedRole = row;
        break;

      default:
        break;
    }

    this.openEditModal();
  }

  setUserFormValues(user: AdminUserView) {
    const controls = this.userForm.controls;
    controls.firstName.setValue(user.firstName.trim());
    controls.lastName.setValue(user.lastName.trim());
    controls.email.setValue(user.email.trim());
    controls.upn.setValue(user.upn.trim());
    controls.groupSelector.setValue(user.groupSelections);
    controls.roleSelector.setValue(user.roleSelections);
    controls.active.setValue(user.active);
    // $('.selectpicker').selectpicker('refresh');

    if (this.userForm.contains('edocsAlias')) {
      controls.edocsAlias.setValue(user.edocsAlias.trim());
    }
  }

  setGroupFormValues(group: Group) {
    const controls = this.groupForm.controls;
    controls.groupName.setValue(group.name.trim());
    controls.groupEmail.setValue(group.email?.trim());
    controls.groupActive.setValue(group.active);
    // $('.selectpicker').selectpicker('refresh');
  }

  setRoleFormValues(role: Role) {
    const controls = this.roleForm.controls;
    controls.groupRef.setValue(role.group_RefId);
    controls.roleName.setValue(role.name.trim());
    controls.roleEmail.setValue(role.email?.trim());
    controls.roleActive.setValue(role.active);
    // $('.selectpicker').selectpicker('refresh');
  }

  // waitForSelectPickerLoad(callback: Function, callBackPayload: any) {
  //   const waitForSelectPicker = setInterval(() => {
  //     if ($('.selectpicker').length > 0) {
  //       clearInterval(waitForSelectPicker);
  //       callback(callBackPayload);
  //     }
  //   }, 100);
  // }

  applyCustomGridFilterEventListener() {
    const waitForGridFilter = setInterval(() => {
      if ($(".e-filtermenudiv.e-icons.e-icon-filter").length > 0) {
        clearInterval(waitForGridFilter);

        document.addEventListener('keyup', (event: any) => {
          if (event.key === "Escape") {
            this.closeCustomFilterPanel();
          }
        });

        document.addEventListener('mouseup', (event: any) => {
          if (event.target.className === 'e-filtermenudiv e-icons e-icon-filter') {
            let filter = event.target;

            if (filter.parentNode?.outerText === 'Groups' || filter.parentNode?.outerText === 'Roles') {
              this.handleCustomGridFilterClick(filter.parentNode?.outerText, filter);
            } else {
              this.closeCustomFilterPanel();
            }
          } else {
            let classNameToSearch = "";
            Array.from(event.target.classList).forEach(className => classNameToSearch += `.${className}`);

            // only close if the click event happens on content not within the custom filter window
            if ($(`#customFilterPopup`).has(`${classNameToSearch}`).length === 0) {
              this.closeCustomFilterPanel();
            }
          }
        });
      }
    });
  }

  closeCustomFilterPanel() {
    if (this.isCustomFilterOpen) {
      this.isCustomFilterOpen = false;
      this.filterModalTarget = CustomAdminFilterModalTargets.None;
      this.filterSearchString = "";
      this.noFilterSearchResultsFound = false;
    }
  }

  handleCustomGridFilterClick(target: string, filter: any) {
    // upon opening the filter
    if (!this.isCustomFilterOpen || (this.isCustomFilterOpen && this.filterModalTarget !== target)) {

      // set the filter window target to the new filter
      this.filterModalTarget = target;
      this.isCustomFilterOpen = true;

      // wait for the filter to popup then load the styles and load the entries
      const waitForPopup = setInterval(() => {
        if ($("#customFilterPopup")) {
          this.setCustomFilterStyling(filter)
          this.populateCustomFilterDatasource();

          // focus on the search box on opening
          $("#customFilterSearch").focus();
          clearInterval(waitForPopup);
        }
      });
      return;
    }
  }

  setCustomFilterStyling(filterElement: any) {
    // set the filter styling to place the loaded UI elements underneath the filter toggle button in the header
    let filterBounds = filterElement.getBoundingClientRect();

    let customFilterElement = document.getElementById("customFilterPopup");
    customFilterElement.style.maxHeight = '800px';
    customFilterElement.style.zIndex = '100000';
    customFilterElement.style.width = '255px';
    customFilterElement.style.left = `${filterBounds.right - 255}px`;
    customFilterElement.style.top = `${filterBounds.top + 28}px`;
  }

  populateCustomFilterDatasource() {
    switch (this.filterModalTarget) {
      case CustomAdminFilterModalTargets.Groups:
        this.assignmentMapLoaded = new Map(this.groupAssignmentsMap);
        break;

      case CustomAdminFilterModalTargets.Roles:
        this.assignmentMapLoaded = new Map(this.roleAssignmentsMap);
        break;

      default:
        break;
    }

    this.selectAllCheckSelected = true;
    this.hasOneEntrySelected = false;

    for (let value of this.assignmentMapLoaded.values()) {
      if (!value) {
        this.selectAllCheckSelected = false;
      } else {
        this.hasOneEntrySelected = true;
      }
    }

    this.assignmentMapPreMapFilter = new Map(this.assignmentMapLoaded);
  }

  handleFilterSearch(event: any) {
    this.filterSearchString = event.target.value;
    if (this.filterSearchString.length === 0) {
      this.assignmentMapLoaded = new Map(this.assignmentMapPreMapFilter);
    }

    if (this.filterSearchString.length > 0) {
      let temporaryNewMap: Map<string, boolean> = new Map();
      for (let [key, value] of this.assignmentMapPreMapFilter) {

        if (key.toLowerCase().includes(this.filterSearchString.toLowerCase())) {
          temporaryNewMap.set(key, value);
        }
      }

      this.assignmentMapLoaded = new Map(temporaryNewMap);
    }

    this.noFilterSearchResultsFound = Array.from(this.assignmentMapLoaded).length === 0;
  }

  handleSelectAllClick() {
    this.selectAllCheckSelected = !this.selectAllCheckSelected;
    this.hasOneEntrySelected = this.selectAllCheckSelected;

    for (let key of this.assignmentMapLoaded.keys()) {
      this.assignmentMapLoaded.set(key, this.selectAllCheckSelected);
      this.assignmentMapPreMapFilter.set(key, this.selectAllCheckSelected);
    }
  }

  handleAssignmentEntryChange(assignmentEntry: string) {
    this.assignmentMapLoaded.set(assignmentEntry, !this.assignmentMapLoaded.get(assignmentEntry));
    this.assignmentMapPreMapFilter.set(assignmentEntry, this.assignmentMapLoaded.get(assignmentEntry));

    let allItemsSelected = true;
    let aSelectedEntryExists = false;
    for (let value of this.assignmentMapPreMapFilter.values()) {
      if (!value) {
        allItemsSelected = false;
      } else {
        aSelectedEntryExists = true;
      }
    }

    this.selectAllCheckSelected = allItemsSelected;
    this.hasOneEntrySelected = aSelectedEntryExists;
  }

  clearCustomFilterAssignmentMap() {
    // on clicking to clear the applied filters, reset to the default of having them all selected
    switch (this.filterModalTarget) {
      case CustomAdminFilterModalTargets.Groups:
        for (let key of this.groupAssignmentsMap.keys()) {
          this.groupAssignmentsMap.set(key, true);
        }
        break;

      case CustomAdminFilterModalTargets.Roles:
        for (let key of this.roleAssignmentsMap.keys()) {
          this.roleAssignmentsMap.set(key, true);
        }
        break;

      default:
        break;
    }

    this.handleUsersListFiltering();
    this.closeCustomFilterPanel();
  }

  applyCustomFilterEntries() {
    switch (this.filterModalTarget) {
      case CustomAdminFilterModalTargets.Groups:
        for (let [key, value] of this.assignmentMapPreMapFilter.entries()) {
          this.groupAssignmentsMap.set(key, value);
        }
        break;

      case CustomAdminFilterModalTargets.Roles:
        for (let [key, value] of this.assignmentMapPreMapFilter.entries()) {
          this.roleAssignmentsMap.set(key, value);
        }
        break;

      default:
        break;
    }

    // handle filtering the list here after applying
    this.handleUsersListFiltering();
    this.closeCustomFilterPanel();
  }

  generateSelectedMapEntries(map: Map<string, boolean>) {
    let entries = [];
    for (let [key, value] of map.entries()) {
      if (value) {
        entries.push(key);
      }
    }
    return entries;
  }

  handleUsersListFiltering() {
    let selectedGroupEntries: string[] = this.generateSelectedMapEntries(this.groupAssignmentsMap);
    let areBlankGroupEntriesIncluded = this.groupAssignmentsMap.get(this.BLANKS_KEY);

    let selectedRoleEntries: string[] = this.generateSelectedMapEntries(this.roleAssignmentsMap);
    let areBlankRoleEntriesIncluded = this.roleAssignmentsMap.get(this.BLANKS_KEY);

    this.filterableUsersList = this.users.filter(user =>
      selectedGroupEntries.some(entry => areBlankGroupEntriesIncluded ?
        (user.groupNamesStr.toLowerCase().includes(entry.toLowerCase()) || user.groupNamesStr.length === 0) :
        user.groupNamesStr.toLowerCase().includes(entry.toLowerCase())
      )
      &&
      selectedRoleEntries.some((entry) => areBlankRoleEntriesIncluded ?
        (user.roleNamesStr.toLowerCase().includes(entry.toLowerCase()) || user.roleNamesStr.length === 0) :
        user.roleNamesStr.toLowerCase().includes(entry.toLowerCase())
      )
    );
  }

  openEditModal() {
    this.edocsUser = '';
    this.edocsAlias = '';
    this.modal = this.modalService.open(this.contentManagement, { windowClass: 'edit' });

    switch (this.adminSection) {
      case AdminSections.Users:
        // this.waitForSelectPickerLoad(this.setUserFormValues.bind(this), this.selectedUser);
        this.setUserFormValues(this.selectedUser);
        break;

      case AdminSections.Groups:
        // this.waitForSelectPickerLoad(this.setGroupFormValues.bind(this), this.selectedGroup);
        this.setGroupFormValues(this.selectedGroup);
        break;

      case AdminSections.Roles:
        // this.waitForSelectPickerLoad(this.setRoleFormValues.bind(this), this.selectedRole);
        this.setRoleFormValues(this.selectedRole);
        break;

      default:
        break;
    }
  }

  openAddModal() {
    this.modalViewMode = AdminModalViewMode.Add;
    this.removeUserFormEditControls();

    this.modal = this.modalService.open(this.contentManagement, { windowClass: 'addNew' });

    this.userForm.controls.active.setValue(true);
    this.groupForm.controls.groupActive.setValue(true);
    this.roleForm.controls.roleActive.setValue(true);

    // const waitForSelectPicker = setInterval(() => {
    //   if ($('.selectpicker').length > 0) {
    //     $('.selectpicker').selectpicker('refresh');
    //     clearInterval(waitForSelectPicker);
    //   }
    // }, 100);
  }

  closeModal() {
    if (this.modal) {
      this.modal.close();

      this.userForm.reset();
      this.groupForm.reset();
      this.roleForm.reset();
    }
  }

  extractUserForm() {
    const user: AdminUserView = {
      firstName: (
        (this.userForm.value.firstName instanceof String || typeof this.userForm.value.firstName === 'string') ?
          this.userForm.value.firstName :
          this.userForm.value.firstName.firstName
      ),
      lastName: this.userForm.value.lastName,
      email: this.userForm.value.email,
      edocsAlias: '',
      upn: this.userForm.value.upn,
      groupSelections: this.userForm.value.groupSelector,
      roleSelections: this.userForm.value.roleSelector || [],
      active: this.userForm.value.active
    };

    if (this.modalViewMode === AdminModalViewMode.Edit) {
      user.edocsAlias = this.userForm.value.edocsAlias.edocsAlias ? this.userForm.value.edocsAlias.edocsAlias : this.userForm.value.edocsAlias;
    }

    return user;
  }

  extractGroupForm() {
    return {
      name: this.groupForm.value.groupName,
      email: this.groupForm.value.groupEmail,
      active: this.groupForm.value.groupActive
    };
  }

  extractRoleForm() {
    return {
      group_RefId: this.roleForm.value.groupRef,
      name: this.roleForm.value.roleName,
      email: this.roleForm.value.roleEmail,
      active: this.roleForm.value.roleActive
    };
  }

  handleEntryUpdate() {
    switch (this.adminSection) {
      case AdminSections.Users:
        const updatedUser: AdminUserView = {
          user_Id: this.selectedUser.user_Id,
          ...this.extractUserForm()
        };

        this.store.dispatch(new UpdateUser(updatedUser));
        break;

      case AdminSections.Groups:
        const updatedGroup: Group = {
          group_Id: this.selectedGroup.group_Id,
          ...this.extractGroupForm()
        };

        this.store.dispatch(new UpdateGroup(updatedGroup));
        break;

      case AdminSections.Roles:
        const updatedRole: Role = {
          role_Id: this.selectedRole.role_Id,
          ...this.extractRoleForm()
        };

        this.store.dispatch(new UpdateRole(updatedRole));
        break;

      default:
        break;
    }

    this.closeModal();
  }

  handleEntrySubmission() {
    // handle the new submission
    switch (this.adminSection) {
      case AdminSections.Users:
        this.store.dispatch(new AdminCreateNewUser(this.extractUserForm()));
        break;

      case AdminSections.Groups:
        this.store.dispatch(new AdminCreateNewGroup(this.extractGroupForm()));
        break;

      case AdminSections.Roles:
        this.store.dispatch(new AdminCreateNewRole(this.extractRoleForm()));
        break;

      default:
        break;
    }

    this.closeModal();
  }

  get adminSections(): typeof AdminSections {
    return AdminSections;
  }

  get adminModalViewMode(): typeof AdminModalViewMode {
    return AdminModalViewMode;
  }

  createdUser(): void {
    document.getElementById(this.userGrid.element.id + '_searchbar')?.addEventListener('keyup', (event: any) => {
      // tslint:disable-next-line: deprecation
      this.userGrid.search((event.target as HTMLInputElement).value);
    });

    const searchClearButton = this.userGrid.element.querySelector('.e-search')

      .querySelector(`#${this.userGrid.element.id}_clearbutton`); // getting the clear button
    searchClearButton.addEventListener('click', (e: any) => {

      if (e.target.classList.contains('e-clear-icon')) { //checking the target icon
        this.userGrid.searchSettings.key = '';
      }

    });
  }

  createdGroup(): void {
    document.getElementById(this.groupGrid.element.id + '_searchbar')?.addEventListener('keyup', (event: any) => {
      // tslint:disable-next-line: deprecation
      this.groupGrid.search((event.target as HTMLInputElement).value);
    });

    const searchClearButton = this.groupGrid.element.querySelector('.e-search')

      .querySelector(`#${this.groupGrid.element.id}_clearbutton`); // getting the clear button
    searchClearButton.addEventListener('click', (e: any) => {

      if (e.target.classList.contains('e-clear-icon')) { //checking the target icon
        this.groupGrid.searchSettings.key = '';
      }

    });
  }

  createdRole(): void {
    document.getElementById(this.roleGrid.element.id + '_searchbar')?.addEventListener('keyup', (event: any) => {
      // tslint:disable-next-line: deprecation
      this.roleGrid.search((event.target as HTMLInputElement).value);
    });

    const searchClearButton = this.roleGrid.element.querySelector('.e-search')

      .querySelector(`#${this.roleGrid.element.id}_clearbutton`); // getting the clear button
    searchClearButton.addEventListener('click', (e: any) => {

      if (e.target.classList.contains('e-clear-icon')) { //checking the target icon
        this.roleGrid.searchSettings.key = '';
      }

    });
  }
}
